import { useEffect } from 'react';

// Dev-only registry of keys that already have a handler installed. When
// two hooks register the same key the last one wins (because both add a
// `keydown` listener and the most-recent handler runs after the other),
// which is a quiet footgun. The registry counts active installations
// and console.warns the second time a key gets registered.
//
// In production the registry stays empty (the export below is a no-op)
// so the cost is dev-only and the production bundle tree-shakes the
// console.warn branch.
const KEY_REGISTRY: Map<string, number> = new Map();

function registerKey(key: string): () => void {
  if (import.meta.env.MODE !== 'development') {
    return () => {};
  }
  const count = (KEY_REGISTRY.get(key) ?? 0) + 1;
  KEY_REGISTRY.set(key, count);
  if (count > 1) {
    console.warn(
      `[useKeyboardMap] key "${key}" is registered by ${count} hooks. ` +
        'The most-recently-registered handler runs after the others. ' +
        'Phase 5 (3/n) will surface a priority field if this becomes a real conflict.',
    );
  }
  return () => {
    const next = (KEY_REGISTRY.get(key) ?? 1) - 1;
    if (next <= 0) KEY_REGISTRY.delete(key);
    else KEY_REGISTRY.set(key, next);
  };
}

// Centralized keyboard map.
//
// Replaces the ad-hoc `keydown` listeners that S1 + AppHeader were
// registering individually (P4.D in the rolling review). One hook,
// registered once at the AppShell level, dispatches to handlers based
// on a stable mapping. Screens register handlers via the `keys`
// argument; the hook owns the focus-guard logic (skip when typing in
// inputs, contenteditable, or inside the CommandPalette).
//
// Keys mirror the v2 keyboard map from implementation-plan.md Phase 5.

export type KeyHandler = {
  /** Display name shown in the shortcut sheet. */
  description: string;
  /** What to run on press. Return true if you handled the event. */
  handler: (e: KeyboardEvent) => void | boolean;
};

export type KeyMap = Record<string, KeyHandler>;

/**
 * Compose a keyboard map for the current screen. Pass the same `map`
 * reference (e.g. memoized) to avoid re-registering on every render.
 */
export function useKeyboardMap(map: KeyMap): void {
  useEffect(() => {
    const unregister = Object.keys(map).map(registerKey);
    function onKey(e: KeyboardEvent) {
      const target = e.target instanceof HTMLElement ? e.target : null;
      // Hard-stop guards: typing surfaces own these keypresses. Skip the
      // guard entirely when the event isn't bound to a real element
      // (e.g. window-dispatched synthetic events in tests).
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (target?.isContentEditable) return;
      if (target?.getAttribute('role') === 'searchbox') return;
      // Modifier combos are out of scope for this hook — they belong to
      // the browser / OS. We only handle bare keys.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const handler = map[e.key];
      if (!handler) return;
      const handled = handler.handler(e);
      // Default: treat the bare-key shortcut as consumed (prevents the
      // page from scrolling on Space, etc.).
      if (handled !== false) e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      for (const u of unregister) u();
    };
  }, [map]);
}

/** A stable, mergeable description record for the shortcut sheet. */
export type ShortcutSheetEntry = {
  keys: string[];
  description: string;
};

export const GLOBAL_SHORTCUTS: ShortcutSheetEntry[] = [
  { keys: ['/'], description: 'Open search' },
  { keys: ['?'], description: 'Open this shortcut sheet' },
  { keys: ['j'], description: 'Next item in the active list' },
  { keys: ['k'], description: 'Previous item in the active list' },
  { keys: ['a'], description: 'Acknowledge the focused alert' },
  { keys: ['A'], description: 'Open Signal Analysis for the focused asset' },
  { keys: ['['], description: 'Step the LinkedCursor back one tick' },
  { keys: [']'], description: 'Step the LinkedCursor forward one tick' },
  { keys: ['C'], description: 'Toggle current vs baseline comparison' },
  { keys: ['B'], description: 'Pin / clear the baseline' },
  { keys: ['.'], description: 'Pin the current moment to LinkedCursor' },
  { keys: ['Escape'], description: 'Close popovers / sheets / palettes' },
];
